import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import {
  Shield,
  Target,
  CheckCircle,
  ArrowLeft,
  CreditCard,
  Lock,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [formData, setFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    companyName: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    billingAddress: "",
    city: "",
    state: "",
    zip: "",
  });

  const plans = {
    "reputation-management": {
      id: "reputation-management",
      name: "Reputation Management",
      price: 200,
      icon: Shield,
      features: [
        "24/7 AI-powered monitoring",
        "8+ platform coverage",
        "Review response assistance",
        "Monthly reports",
        "Negative content suppression",
        "Review generation campaigns",
        "Dedicated account manager",
      ],
    },
    "online-marketing": {
      id: "online-marketing",
      name: "Online Marketing Package",
      price: 500,
      icon: Target,
      features: [
        "Everything in Reputation Management",
        "Google Platinum Partner services",
        "AdSense optimization",
        "AI A/B testing (5000+ parameters)",
        "Search result influencing",
        "Positive content creation",
        "Weekly reports",
      ],
      requiresConsultation: true,
    },
  };

  const plan = plans[planId];

  useEffect(() => {
    if (!plan) {
      navigate("/pricing");
    }
    // Redirect to consultation for marketing package
    if (plan?.requiresConsultation) {
      navigate("/book-consultation");
    }
  }, [plan, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    if (!formData.customerName || !formData.customerEmail) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        company_name: formData.companyName,
        plan_id: plan.id,
        plan_name: plan.name,
        plan_price: plan.price,
        billing_cycle: "monthly",
      });

      toast.success(response.data.message);
      navigate(`/?order_success=true&order_id=${response.data.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process order. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!plan) {
    return null;
  }

  const PlanIcon = plan.icon;

  return (
    <div data-testid="checkout-page" className="pt-20 min-h-screen bg-muted/30">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        {/* Back Button */}
        <Link to="/pricing" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" />
          Back to Pricing
        </Link>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Order Form */}
          <div className="lg:col-span-3">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle style={{ fontFamily: "Outfit, sans-serif" }}>
                  Complete Your Order
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-4">Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="customerName">Full Name *</Label>
                        <Input
                          id="customerName"
                          name="customerName"
                          data-testid="checkout-name-input"
                          placeholder="John Smith"
                          value={formData.customerName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customerEmail">Email Address *</Label>
                        <Input
                          id="customerEmail"
                          name="customerEmail"
                          type="email"
                          data-testid="checkout-email-input"
                          placeholder="john@company.com"
                          value={formData.customerEmail}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customerPhone">Phone Number</Label>
                        <Input
                          id="customerPhone"
                          name="customerPhone"
                          data-testid="checkout-phone-input"
                          placeholder="(123) 456-7890"
                          value={formData.customerPhone}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name</Label>
                        <Input
                          id="companyName"
                          name="companyName"
                          data-testid="checkout-company-input"
                          placeholder="Your Company"
                          value={formData.companyName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Payment Information
                    </h3>
                    <div className="p-4 bg-muted/50 rounded-lg mb-4">
                      <p className="text-sm text-muted-foreground">
                        For security, our team will contact you to complete payment setup after order submission.
                        You will not be charged until your account is activated.
                      </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="billingAddress">Billing Address</Label>
                        <Input
                          id="billingAddress"
                          name="billingAddress"
                          data-testid="checkout-address-input"
                          placeholder="123 Main St"
                          value={formData.billingAddress}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          name="city"
                          data-testid="checkout-city-input"
                          placeholder="San Francisco"
                          value={formData.city}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="state">State</Label>
                          <Input
                            id="state"
                            name="state"
                            data-testid="checkout-state-input"
                            placeholder="CA"
                            value={formData.state}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="zip">ZIP Code</Label>
                          <Input
                            id="zip"
                            name="zip"
                            data-testid="checkout-zip-input"
                            placeholder="94102"
                            value={formData.zip}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="terms"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      data-testid="checkout-terms-checkbox"
                    />
                    <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                      I agree to the Terms of Service and Privacy Policy. I understand that I will be 
                      contacted to complete payment setup before my account is activated.
                    </label>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isLoading || !agreedToTerms}
                    data-testid="checkout-submit-btn"
                    className="w-full bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full py-6 text-lg font-semibold"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Lock className="mr-2 h-5 w-5" />
                        Place Order - ${plan.price}/month
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Secure checkout. Your information is protected.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <Card className="bg-card border-border sticky top-24">
              <CardHeader>
                <CardTitle style={{ fontFamily: "Outfit, sans-serif" }}>
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
                  <div className="w-14 h-14 rounded-xl bg-[#F37021]/10 flex items-center justify-center">
                    <PlanIcon className="h-7 w-7 text-[#F37021]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground">Monthly subscription</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-[#F37021] flex-shrink-0" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium">${plan.price}.00</span>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-muted-foreground">Billing</span>
                    <span className="font-medium">Monthly</span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-border">
                    <span className="font-bold text-foreground">Total</span>
                    <span className="text-2xl font-bold text-[#F37021]">${plan.price}/mo</span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    Your subscription begins after payment is confirmed. Cancel anytime.
                    30-day money-back guarantee.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
